import React, { useEffect, useRef, ReactNode } from 'react'

import { useTransition, animated, UseTransitionProps } from '@react-spring/web'
import classNames from 'classnames'

import { MODAL_CONTENT_CLASS } from '@web/libs/constants'

const generateBackgroundStyle = ({ position, onRest }: Pick<Props, 'position' | 'onRest'>) => {
  if (position === 'bottom')
    return {
      config: {
        tension: 300,
        clamp: true,
      },
      from: { opacity: 0, transform: 'translate(0, 100%)' },
      enter: { opacity: 1, transform: 'translate(0, 0)' },
      leave: { opacity: 0, transform: 'translate(0, 100%)' },
      onRest,
    }

  if (position === 'full')
    return {
      config: {
        tension: 300,
        clamp: true,
      },
      from: { opacity: 0 },
      enter: { opacity: 1 },
      leave: { opacity: 0 },
      onRest,
    }

  return {
    config: {
      tension: 300,
    },
    from: { opacity: 0, transform: 'translate(-50%, -100%)' },
    enter: { opacity: 1, transform: 'translate(-50%, -50%)' },
    leave: { opacity: 0, transform: 'translate(-50%, -100%)' },
    onRest,
  }
}

interface Props {
  showModal: boolean
  className?: string
  position?: 'bottom' | 'top' | 'full'
  role?: 'dialog' | 'alert'
  onRest?: UseTransitionProps['onRest']
  children?: ReactNode
}

const ModalContainer = ({ children, showModal, position, className, role = 'dialog', onRest }: Props) => {
  const contentStyles = generateBackgroundStyle({ position, onRest })
  const contentTransitions = useTransition(showModal, contentStyles)

  const modalRef = useRef<HTMLDivElement>(null)
  const prevFocusedElementRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!modalRef.current) return

    if (showModal) {
      prevFocusedElementRef.current = document.activeElement as HTMLElement
      modalRef.current.focus()
    } else {
      prevFocusedElementRef.current?.focus()
      prevFocusedElementRef.current = null
    }
  }, [showModal])

  return contentTransitions(
    (style, item) =>
      item && (
        <animated.div
          ref={modalRef}
          role={role}
          className={classNames(MODAL_CONTENT_CLASS, className, { place_modal_content_fullpage: position === 'full' })}
          style={style as any}
          tabIndex={-1}
        >
          {children}
        </animated.div>
      ),
  )
}

export default ModalContainer
